import React, { Component } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import memoize from 'lodash/memoize';
import { SuiContainer, SuiCollapsible } from '@splashthat/sui';

import {
    INPUT_TYPE_HIDDEN,
    INPUT_TYPE_CHECKBOX,
    INPUT_TYPE_DATE,
    INPUT_TYPE_NUMBER,
    INPUT_TYPE_FILE,
} from 'common/entities/formNode/inputTypes';
import {
    getNodeLockingAllowed,
    getBootstrap,
    getEditFormNodeId,
    getFormId,
} from 'common/compounds/FormBuilder/selectors';
import { getFormNode } from 'common/entities/formNode/selectors';
import { NODE_NAME_CC_EMAIL, NODE_NAME_BCC_EMAIL } from 'common/entities/formNode/nodeNames';
import { isFormTypeTicketSale } from 'common/entities/form/selectors';

import FormEditorEditQuestionOptions from './FormEditorEditQuestionOptions';
import EditViewSectionContainer from './EditViewSectionContainer';
import RichTextLabelControl from './components/RichTextLabelControl';
import FieldNameControl from './components/FieldNameControl';
import InputTypeControl from './components/InputTypeControl';
import DateRangeControl from './components/DateRangeControl';
import RequiredControl from './components/RequiredControl';
import CheckboxAttachmentControl from './components/CheckboxAttachmentControl';
import CheckboxDefaultValueControl from './components/CheckboxDefaultValueControl';
import UrlParamControl from './components/UrlParamControl';
import PlaceholderTextControl from './components/PlaceholderTextControl';
import SectionSeparator from './components/SectionSeparator';
import LockControl from './components/LockControl';
import MultiChoiceControls from './components/MultiChoiceControls';
import DefaultValueControl from './components/DefaultValueControl';
import HelperTextControl from './components/HelperTextControl';
import PersonalizationTagControl from './components/PersonalizationTagControl';
import ReservedNodeIndication from './components/ReservedNodeIndication';
import NumberRange from './components/NumberRange';

import getReservedInputNameLabel from '../../utils/getReservedInputNameLabel';
import { INPUT_TYPE_LOCKED_NODE_NAMES, REQUIRED_LOCKED_NODE_NAMES } from '../../constants/nodeNameRules';
import { PLACEHOLDER_TEXT_ALLOWED_INPUT_TYPES } from '../../constants/nodeInputTypeRules';
import { INPUT_TYPE_SHORT_NAMES, INPUT_TYPE_CONFIGURATIONS } from '../../constants/bootstrapKeys';

import { isTemporaryId } from '../../utils/formValidations';
import { updateFormNodeValue, updateFormNodeLabel } from '../../actions/nodeActions';
import { updateFormNodeOption } from '../../actions/nodeOptionActions';

export const ADVANCED_COLLAPSIBLE_HEADER = 'Advanced';
export const FILE_INPUT_WARNING =
    'Make sure not to upload or request health information, unique identification numbers (like a driver’s license, Social Security, or passport number) financial data, or other types of confidential personal information.';

const formatOptionsCollapsibleHeader = (inputTypeShortName) => `${inputTypeShortName} Options`.trim();

const canChangeInputType = memoize((name) => !INPUT_TYPE_LOCKED_NODE_NAMES.includes(name));
const canChangeRequired = memoize((name) => !REQUIRED_LOCKED_NODE_NAMES.includes(name));
const canChangePlaceholderText = (inputType) => Boolean(PLACEHOLDER_TEXT_ALLOWED_INPUT_TYPES[inputType]);
const canChangeDefaultValue = (inputType) => inputType === INPUT_TYPE_HIDDEN;

const getInputWarning = (inputType) => {
    const warnings = {
        [INPUT_TYPE_FILE]: FILE_INPUT_WARNING,
    };

    return warnings[inputType];
};

const getReservedIndicatorTooltipByName = (formNodeName) => {
    const tooltipContents = {
        [NODE_NAME_CC_EMAIL]: "CC/BCC'd email addresses will receive all event emails along with the guest.",
        [NODE_NAME_BCC_EMAIL]: "CC/BCC'd email addresses will receive all event emails along with the guest.",
    };
    return tooltipContents[formNodeName];
};

class FormEditorEditQuestion extends Component {
    static propTypes = {
        // Question Entity Properties
        formNodeId: PropTypes.string.isRequired,
        name: PropTypes.string,
        code: PropTypes.string,
        inputType: PropTypes.string,
        config: PropTypes.shape({
            options: PropTypes.object,
            includeOther: PropTypes.bool,
            linkUrl: PropTypes.string,
            linkLabel: PropTypes.string,
        }),
        nodeLockingAllowed: PropTypes.bool.isRequired,
        bootstrapInputTypeConfigurations: PropTypes.object.isRequired,
        bootstrapInputTypeShortNames: PropTypes.object.isRequired,
        showUrlParamControl: PropTypes.bool.isRequired,

        updateFormNodeValue: PropTypes.func.isRequired,
        updateFormNodeOption: PropTypes.func.isRequired,
        updateFormNodeLabel: PropTypes.func.isRequired,
    };

    static defaultProps = {
        label: '',
        labelMarkup: '',
        name: '',
        code: '',
        inputType: '',
        config: {},
        errors: [],
    };

    state = {
        /** @var {bool} - determines if value inputs should show. Not stored in app data. */
        showEditValueInputs: this.areOptionLabelsAndValuesDifferent(this.props.config.options),
        isFieldNameTouched: !!this.props.code,
    };

    hasOptions() {
        const { bootstrapInputTypeConfigurations } = this.props;
        return !!get(bootstrapInputTypeConfigurations, [this.props.inputType, 'options']);
    }

    /**
     * @return {String}
     */
    getOptionsCollapsibleHeader(bootstrapInputTypeShortNames, inputType) {
        const shortName = this.props.bootstrapInputTypeShortNames[this.props.inputType] || '';
        return formatOptionsCollapsibleHeader(shortName);
    }

    handleChangeFieldName = (value) => {
        if (isTemporaryId(this.props.formNodeId) && this.props.inputType === INPUT_TYPE_HIDDEN) {
            this.props.updateFormNodeLabel(this.props.formNodeId, value, '');
        }
        this.setState({ isFieldNameTouched: !!value });
    };

    handleChangeLabel = (label, labelMarkup) => {
        if (isTemporaryId(this.props.formNodeId) && !this.state.isFieldNameTouched) {
            this.props.updateFormNodeValue(this.props.formNodeId, 'code', label);
        }
    };

    handleChangeValuesHiddenCheckbox = (isChecked) => {
        this.setState({ showEditValueInputs: !isChecked });

        // If checked, sync all values with their labels.
        if (isChecked) {
            // @TODO: make this a new action that sets all option values to labels.
            const entities = get(this.props.config, 'options.entities', {});
            Object.values(entities)
                .filter((option) => option.value !== option.label)
                .forEach((option) => {
                    this.props.updateFormNodeOption(this.props.formNodeId, option.id, { value: option.label });
                });
        }
    };

    /**
     * Determines if all options have matching label and value.
     * @param {array} [options]
     * @return {bool}
     */
    areOptionLabelsAndValuesDifferent(options = {}) {
        const { entities, result } = options;
        return !!(result || []).find((id) => entities[id].label !== entities[id].value);
    }

    /**
     * @param {React[]} nodes
     * @param {React} SeparatorComponent - separator to add between nodes.
     * @return {React[]}
     */
    addComponentInBetweenNodes(nodes, SeparatorComponent) {
        return nodes
            .filter(Boolean)
            .reduce(
                (memo, node, index) => [
                    ...memo,
                    node,
                    index !== nodes.length - 1 ? <SeparatorComponent key={index} /> : null,
                ],
                [],
            );
    }

    renderAdvancedSection() {
        const advanced = [];

        const { formNodeId, nodeLockingAllowed, showUrlParamControl } = this.props;

        const hasOptions = this.hasOptions();

        advanced.push(
            <SuiContainer variation="flex-group" key="helper-text">
                <HelperTextControl formNodeId={formNodeId} />
            </SuiContainer>,
        );

        if (hasOptions) {
            advanced.push(
                <SuiContainer variation="flex-group" key="values-hidden">
                    <MultiChoiceControls
                        formNodeId={formNodeId}
                        showEditValueInputs={this.state.showEditValueInputs}
                        onShowEditValueInputsChange={this.handleChangeValuesHiddenCheckbox}
                    />
                </SuiContainer>,
            );
        }

        if (showUrlParamControl) {
            advanced.push(
                <SuiContainer variation="flex-group" key="url-params">
                    <UrlParamControl formNodeId={formNodeId} />
                </SuiContainer>,
            );
        }

        if (nodeLockingAllowed) {
            advanced.push(
                <SuiContainer variation="flex-group" key="lock">
                    <LockControl formNodeId={formNodeId} />
                </SuiContainer>,
            );
        }

        if (!advanced.length) {
            return null;
        } else {
            return (
                <SuiCollapsible
                    fullWidth
                    header={ADVANCED_COLLAPSIBLE_HEADER}
                    content={
                        <EditViewSectionContainer>
                            {this.addComponentInBetweenNodes(advanced, SectionSeparator)}
                        </EditViewSectionContainer>
                    }
                />
            );
        }
    }

    render() {
        const { formNodeId, name } = this.props;

        const reservedNodeName = getReservedInputNameLabel(name);

        const hasOptions = this.hasOptions();
        const showEditValueInputs = this.state.showEditValueInputs && hasOptions;

        const isHiddenInputType = this.props.inputType === INPUT_TYPE_HIDDEN;

        const getComponentByInputType = (inputType) => {
            const components = {
                [INPUT_TYPE_CHECKBOX]: (
                    <React.Fragment>
                        <CheckboxDefaultValueControl formNodeId={formNodeId} />
                        <CheckboxAttachmentControl formNodeId={formNodeId} />
                    </React.Fragment>
                ),
                [INPUT_TYPE_DATE]: <DateRangeControl formNodeId={formNodeId} />,
                [INPUT_TYPE_NUMBER]: <NumberRange formNodeId={formNodeId} />,
                default: null,
            };

            return components[inputType] || components.default;
        };

        const inputWarning = getInputWarning(this.props.inputType);

        return (
            <SuiContainer>
                {canChangeRequired(name) && <RequiredControl />}
                <EditViewSectionContainer>
                    {canChangeInputType(name) && (
                        <React.Fragment>
                            <SuiContainer variation="flex-group">
                                <InputTypeControl formNodeId={formNodeId} />
                            </SuiContainer>
                            {inputWarning && <SuiContainer>{inputWarning}</SuiContainer>}
                            <SectionSeparator />
                        </React.Fragment>
                    )}

                    {!isHiddenInputType && (
                        <SuiContainer variation="flex-group">
                            <RichTextLabelControl formNodeId={formNodeId} onChange={this.handleChangeLabel} />
                        </SuiContainer>
                    )}

                    {reservedNodeName && (
                        <React.Fragment>
                            <ReservedNodeIndication
                                label={reservedNodeName}
                                tooltip={getReservedIndicatorTooltipByName(name)}
                            />
                        </React.Fragment>
                    )}

                    <SuiContainer variation="flex-group">
                        <FieldNameControl
                            formNodeId={formNodeId}
                            onChange={this.handleChangeFieldName}
                            isHiddenInputType={isHiddenInputType}
                        />
                    </SuiContainer>
                    {[
                        canChangeDefaultValue(this.props.inputType) && <DefaultValueControl formNodeId={formNodeId} />,
                        <PersonalizationTagControl key="personalization-tag" formNodeId={formNodeId} />,
                        canChangePlaceholderText(this.props.inputType) && (
                            <PlaceholderTextControl formNodeId={formNodeId} />
                        ),
                        getComponentByInputType(this.props.inputType),
                    ]
                        .filter(Boolean)
                        .map((ControlComponent, i) => (
                            <React.Fragment key={i}>
                                <SectionSeparator />
                                <SuiContainer variation="flex-group">{ControlComponent}</SuiContainer>
                            </React.Fragment>
                        ))}
                </EditViewSectionContainer>

                {hasOptions && (
                    <SuiCollapsible
                        fullWidth
                        defaultIsOpen
                        header={this.getOptionsCollapsibleHeader()}
                        content={
                            <EditViewSectionContainer>
                                <FormEditorEditQuestionOptions showEditValueInputs={showEditValueInputs} />
                            </EditViewSectionContainer>
                        }
                    />
                )}

                {this.renderAdvancedSection()}
            </SuiContainer>
        );
    }
}

const mapStateToProps = (state) => {
    const nodeLockingAllowed = getNodeLockingAllowed(state);
    const formNodeId = getEditFormNodeId(state);
    const formNode = getFormNode(state, formNodeId);
    const showUrlParamControl = !isFormTypeTicketSale(state, getFormId(state));

    const {
        [INPUT_TYPE_SHORT_NAMES]: bootstrapInputTypeShortNames,
        [INPUT_TYPE_CONFIGURATIONS]: bootstrapInputTypeConfigurations,
    } = getBootstrap(state);

    return {
        formNodeId,
        formNodeType: get(formNode, 'type', ''),
        name: formNode ? formNode.name : undefined,
        code: formNode ? formNode.code : undefined,
        inputType: formNode ? formNode.inputType : undefined,
        config: formNode ? formNode.config : undefined,
        nodeLockingAllowed,
        bootstrapInputTypeShortNames,
        bootstrapInputTypeConfigurations,
        showUrlParamControl,
    };
};

const mapDispatchToProps = {
    updateFormNodeOption,
    updateFormNodeValue,
    updateFormNodeLabel,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormEditorEditQuestion);
